import React, { useState, useEffect } from 'react';
import {
    IconButton,
    Badge,
    Popover,
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    Divider,
    Button,
    Stack,
    Chip,
    CircularProgress,
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    NotificationsNone as NotificationsNoneIcon,
    Circle as CircleIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { NotificationResponse, NotificationPriority } from '../interfaces/INotification';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useApiClient } from '../ApiClientProvider';
import { NotificationService, NotificationScope } from '../services/notificationService';

interface NotificationBellProps {
    unreadCount: number;
    scope: NotificationScope;
    onCountChange?: (count: number) => void;
    title?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = React.memo(({ unreadCount, scope, onCountChange, title = 'Notifications' }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const navigate = useNavigate();
    const apiClient = useApiClient('applicationClient');
    const notificationService = React.useMemo(
        () => new NotificationService(apiClient, scope),
        [apiClient, scope],
    );

    const open = Boolean(anchorEl);
    const hasUnreadInLoadedNotifications = notifications.some((n) => !n.isRead);

    const parseApiDate = (value: string): Date => {
        const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
        return new Date(hasTimezone ? value : `${value}Z`);
    };

    const fetchNotifications = React.useCallback(async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await notificationService.getNotifications(pageNum, 20, true);

            if (pageNum === 1) {
                setNotifications(response.items);
            } else {
                setNotifications((prev) => [...prev, ...response.items]);
            }

            setHasMore(response.items.length === 20);
            setPage(pageNum);

            // Keep the bell badge/count in sync even if a SignalR event is missed.
            if (pageNum === 1 && onCountChange) {
                const latestUnreadCount = await notificationService.getUnreadCount();
                onCountChange(latestUnreadCount);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [notificationService, onCountChange]);

    const markAsRead = async (notificationId: string) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications((prev) =>
                prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
            );

            // Use server value to avoid local count drift.
            if (onCountChange) {
                const latestUnreadCount = await notificationService.getUnreadCount();
                onCountChange(latestUnreadCount);
            }
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
            // Reset count to 0 if callback is provided
            if (onCountChange) {
                onCountChange(0);
            }
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    // Fetch notifications when the popover opens, and reconcile resolved work while it stays open.
    useEffect(() => {
        if (!open) return;
        fetchNotifications(1);
        const handleResync = () => fetchNotifications(1);
        const handleAttentionChanged = () => fetchNotifications(1);
        window.addEventListener('odyssey:notifications-resync', handleResync);
        window.addEventListener('odyssey:attention-changed', handleAttentionChanged);
        return () => {
            window.removeEventListener('odyssey:notifications-resync', handleResync);
            window.removeEventListener('odyssey:attention-changed', handleAttentionChanged);
        };
    }, [open, fetchNotifications]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const resolveNotificationNavigationTarget = (notification: NotificationResponse) => {
        if (!notification.actionUrl) {
            return null;
        }

        try {
            const resolvedUrl = new URL(notification.actionUrl, window.location.origin);
            if (resolvedUrl.protocol !== 'http:' && resolvedUrl.protocol !== 'https:') {
                return null;
            }

            const isInternalUrl = resolvedUrl.origin === window.location.origin;

            if (
                isInternalUrl
                && notification.relatedEntityType === 'CommunityPost'
                && notification.relatedEntityId
                && !resolvedUrl.searchParams.has('postId')
            ) {
                resolvedUrl.searchParams.set('postId', notification.relatedEntityId);
            }

            return {
                url: isInternalUrl
                    ? `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`
                    : resolvedUrl.href,
                isExternal: !isInternalUrl,
            };
        } catch {
            return null;
        }
    };

    const handleNotificationClick = async (notification: NotificationResponse) => {
        // Mark as read
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }

        const navigationTarget = resolveNotificationNavigationTarget(notification);
        if (!navigationTarget) {
            return;
        }

        if (navigationTarget.isExternal) {
            window.location.assign(navigationTarget.url);
        } else {
            navigate(navigationTarget.url);
        }

        handleClose();
    };

    const handleMarkAllAsRead = async () => {
        await markAllAsRead();
    };

    const handleLoadMore = () => {
        fetchNotifications(page + 1);
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case NotificationPriority.Urgent:
                return 'error';
            case NotificationPriority.High:
                return 'warning';
            default:
                return 'default';
        }
    };

    return (
        <>
            <IconButton
                onClick={handleClick}
                sx={{ color: 'inherit' }}
                aria-label={title}
            >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 420,
                            maxHeight: 600,
                            mt: 1.5,
                        },
                    },
                }}
            >
                <Box sx={{ p: 2, pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{title}</Typography>
                        {(unreadCount > 0 || hasUnreadInLoadedNotifications) && (
                            <Button
                                size="small"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={handleMarkAllAsRead}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </Stack>
                </Box>

                <Divider />

                {notifications.length === 0 && !loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            No {title.toLowerCase()} yet
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <List sx={{ p: 0, maxHeight: 480, overflow: 'auto' }}>
                            {notifications.map((notification) => (
                                <React.Fragment key={notification.id}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => handleNotificationClick(notification)}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                backgroundColor: notification.isRead
                                                    ? 'transparent'
                                                    : 'action.hover',
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.5} width="100%">
                                                {!notification.isRead && (
                                                    <CircleIcon
                                                        sx={{
                                                            fontSize: 8,
                                                            color: 'primary.main',
                                                            mt: 1,
                                                        }}
                                                    />
                                                )}
                                                <Box flex={1}>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="flex-start"
                                                        spacing={1}
                                                        mb={0.5}
                                                    >
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: notification.isRead ? 400 : 600,
                                                            }}
                                                        >
                                                            {notification.title}
                                                        </Typography>
                                                        {notification.priority !== NotificationPriority.Normal && (
                                                            <Chip
                                                                label={notification.priority}
                                                                size="small"
                                                                color={getPriorityColor(notification.priority)}
                                                                sx={{ height: 20 }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {notification.message}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                                                        {formatDistanceToNow(parseApiDate(notification.updatedAt ?? notification.createdAt), {
                                                            addSuffix: true,
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </ListItemButton>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {hasMore && !loading && notifications.length > 0 && (
                            <Box sx={{ p: 1 }}>
                                <Button fullWidth onClick={handleLoadMore} size="small">
                                    Load More
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Popover>
        </>
    );
});

NotificationBell.displayName = 'NotificationBell';
